import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, ExternalLink, FlaskConical, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { BETA_SPECS, METHOD_LABEL, type BetaKey, type BetaSpec } from './beta-channels'
import type { ChannelDef } from './channel-catalog'

/**
 * Trang thiết lập kênh BETA — đủ các bước, trường cần nhập và cảnh báo để
 * duyệt giao diện trước khi làm backend. KHÔNG gửi dữ liệu đi đâu: nút kết
 * nối bị khoá, form chỉ là state cục bộ (không lưu token/mật khẩu).
 */
export function BetaChannelSetup({ def }: { def: ChannelDef }) {
  const { t } = useTranslation('chanEvotech')
  const spec: BetaSpec = BETA_SPECS[def.key as BetaKey]
  const [values, setValues] = useState<Record<string, string>>({})

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex gap-3 rounded-lg border border-primary/20 bg-primary/5 p-4 text-sm">
        <FlaskConical className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div>
          <p className="font-medium">{t('betaSetup.title')}</p>
          <p className="mt-0.5 text-muted-foreground">
            {t('betaSetup.desc', { name: def.name })}
          </p>
        </div>
      </div>

      {spec.risk && (
        <div className="flex gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <div>
            <p className="font-medium text-destructive">{t('betaSetup.riskTitle')}</p>
            <p className="mt-0.5 text-muted-foreground">{spec.risk}</p>
          </div>
        </div>
      )}

      <div className="rounded-lg border bg-card">
        <dl className="grid gap-x-6 gap-y-3 p-4 text-sm sm:grid-cols-[140px_1fr]">
          <dt className="text-muted-foreground">API</dt>
          <dd className="flex flex-wrap items-center gap-2">
            {spec.api}
            {spec.docsUrl && (
              <a
                href={spec.docsUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                {t('betaSetup.docs')} <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </dd>
          <dt className="text-muted-foreground">{t('betaSetup.method')}</dt>
          <dd>
            <Badge variant="secondary">{METHOD_LABEL[spec.method]}</Badge>
          </dd>
          <dt className="text-muted-foreground">{t('betaSetup.inbound')}</dt>
          <dd>{spec.inbound}</dd>
        </dl>

        <div className="border-t p-4">
          <h3 className="mb-3 text-sm font-medium">{t('betaSetup.steps')}</h3>
          <ol className="space-y-2.5">
            {spec.steps.map((step, i) => (
              <li key={i} className="flex gap-3 text-sm">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>

        {spec.fields && (
          <div className="grid gap-3 border-t p-4 sm:grid-cols-2">
            {spec.fields.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={`beta-${f.key}`}>{f.label}</Label>
                <Input
                  id={`beta-${f.key}`}
                  type={f.secret ? 'password' : 'text'}
                  autoComplete="off"
                  placeholder={f.placeholder}
                  value={values[f.key] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                />
                {f.help && <p className="text-xs text-muted-foreground">{f.help}</p>}
              </div>
            ))}
          </div>
        )}

        {spec.notes && (
          <ul className="space-y-1.5 border-t p-4 text-xs text-muted-foreground">
            {spec.notes.map((n) => (
              <li key={n} className="flex gap-2">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {n}
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-center justify-end gap-3 border-t p-4">
          <span className="text-xs text-muted-foreground">{t('betaSetup.footer')}</span>
          <Button disabled>{spec.connectLabel}</Button>
        </div>
      </div>
    </div>
  )
}
